// 1. useState import add kiya
import React, { useState } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion'; // AnimatePresence zaroori hai popup ke liye
import { Dumbbell, Activity, ShieldCheck, MapPin, Phone, CheckCircle2, Users, Star, ArrowRight, X, Mail } from 'lucide-react';

const BasicGymLanding = () => {
  // === 2. Popup State ===
  const [isFormOpen, setIsFormOpen] = useState(false);

const handleOpenForm = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault(); 
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
  };

  // Animation Variants (Pichla TypeScript issue resolved via explicit typing)
  const fadeUp: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const staggerContainer: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen font-sans selection:bg-green-500 selection:text-black overflow-x-hidden">
      
      {/* Navbar (Glassmorphism) */}
      <nav className="fixed top-0 w-full z-50 bg-black/50 backdrop-blur-md border-b border-white/10 px-6 lg:px-12 py-4 flex justify-between items-center">
        <div className="flex items-center gap-2 text-2xl font-black text-green-500 tracking-tighter uppercase">
          <Dumbbell size={32} />
          Fit<span className="text-white">Core</span>
        </div>
        {/* === 3. Trigger added here === */}
        <button 
          onClick={handleOpenForm}
          className="bg-green-500 text-black font-bold py-2 px-6 rounded-full hover:bg-green-400 transition-all hover:scale-105 shadow-[0_0_15px_rgba(34,197,94,0.4)]"
        >
          Join Now
        </button>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 px-6 lg:px-12 text-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=1470&auto=format&fit=crop" 
            alt="Gym Background" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-[#0a0a0a]"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}
            className="inline-block border border-green-500/30 bg-green-500/10 backdrop-blur-sm text-green-400 px-4 py-1.5 rounded-full text-sm font-bold tracking-wider mb-6 uppercase"
          >
            🔥 The Best Gym In Town
          </motion.div>
          
          <motion.h1 
            initial="hidden" animate="visible" variants={fadeUp}
            className="text-5xl lg:text-8xl md:text-7xl font-black tracking-tight mb-6 uppercase leading-tight"
          >
            Transform Your <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-green-600 drop-shadow-[0_0_10px_rgba(34,197,94,0.5)]">
              Body & Mind
            </span>
          </motion.h1>
          
          <motion.p 
            initial="hidden" animate="visible" variants={fadeUp} transition={{ delay: 0.2 }}
            className="text-zinc-300 text-lg lg:text-2xl max-w-2xl mx-auto mb-10 font-light"
          >
            Don't wait for tomorrow. Get access to premium equipment, expert trainers, and a vibe that makes you want to lift heavier.
          </motion.p>
          
          <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ delay: 0.4 }} className="flex flex-col sm:flex-row gap-4 justify-center">
             {/* === 3. Trigger added here (as href link) === */}
             <a href="#" onClick={handleOpenForm} className="bg-green-500 text-black font-black text-lg py-4 px-10 rounded-full hover:bg-green-400 transition-all hover:scale-105 shadow-[0_0_20px_rgba(34,197,94,0.5)] flex items-center justify-center gap-2">
               View Plans <ArrowRight size={20} />
             </a>
             <a href="#plans" className="bg-white/10 backdrop-blur-md border border-white/20 text-white font-bold text-lg py-4 px-10 rounded-full hover:bg-white/20 transition-all">
               Our Programs
             </a>
          </motion.div>
        </div>
      </section>

      {/* Stats Section (Same as before) */}
      <section className="relative z-20 -mt-16 px-6 lg:px-12 max-w-6xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="bg-zinc-900/80 backdrop-blur-lg border border-white/10 rounded-3xl p-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center shadow-2xl"
        >
          {[
            { icon: <Users size={30}/>, num: "500+", text: "Active Members" },
            { icon: <Dumbbell size={30}/>, num: "50+", text: "Premium Machines" },
            { icon: <ShieldCheck size={30}/>, num: "15+", text: "Pro Trainers" },
            { icon: <Star size={30}/>, num: "4.9/5", text: "Google Rating" }
          ].map((stat, i) => (
            <div key={i} className="flex flex-col items-center justify-center space-y-2">
              <div className="text-green-500">{stat.icon}</div>
              <h3 className="text-3xl font-black">{stat.num}</h3>
              <p className="text-zinc-400 text-sm font-medium uppercase tracking-wider">{stat.text}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Programs Section (Same as before) */}
      <section id="plans" className="py-24 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tight mb-4">Our <span className="text-green-500">Programs</span></h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">Everything you need to hit your fitness goals under one roof.</p>
        </div>
        <motion.div 
          variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {[
            { title: "Strength Training", img: "https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?q=80&w=1000&auto=format&fit=crop" },
            { title: "Cardio Zone", img: "https://images.unsplash.com/photo-1538805060514-97d9cc17730c?q=80&w=1000&auto=format&fit=crop" },
            { title: "Personal Coaching", img: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=80&w=1000&auto=format&fit=crop" }
          ].map((item, idx) => (
            <motion.div key={idx} variants={fadeUp} className="relative group h-96 rounded-3xl overflow-hidden cursor-pointer" onClick={handleOpenForm}> {/* === NAYA: Click to open form === */}
              <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-8 w-full">
                <h3 className="text-2xl font-bold mb-2 translate-y-4 group-hover:translate-y-0 transition-transform duration-300">{item.title}</h3>
                <p className="text-green-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2">
                  Learn More <ArrowRight size={16}/>
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Pricing Section (triggers added) */}
      <section className="py-20 px-6 lg:px-12 bg-zinc-950 border-y border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tight mb-4">Membership <span className="text-green-500">Plans</span></h2>
            <p className="text-zinc-400 text-lg">Simple pricing. No hidden fees. Just results.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
             {/* Standard Plan */}
             <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="p-8 rounded-3xl bg-zinc-900 border border-white/10 hover:border-white/30 transition-colors">
               <h3 className="text-2xl font-bold mb-2 text-zinc-300">Monthly Pass</h3>
               <div className="text-5xl font-black text-white mb-2">₹999<span className="text-lg text-zinc-500 font-normal">/mo</span></div>
               <p className="text-zinc-400 mb-8 pb-8 border-b border-white/10">Perfect for getting started.</p>
               <ul className="space-y-4 mb-8">
                 {['Full Gym Access', 'Cardio & Strength Area', 'Locker Facility', 'Free Diet Chart'].map((f, i) => (
                   <li key={i} className="flex items-center gap-3 text-zinc-300"><CheckCircle2 size={20} className="text-green-500" /> {f}</li>
                 ))}
               </ul>
               {/* === 3. Trigger added here === */}
               <button onClick={handleOpenForm} className="w-full py-4 rounded-xl border border-white/20 text-white font-bold hover:bg-white/10 transition-colors">Choose Monthly</button>
             </motion.div>
             
             {/* Premium Plan */}
             <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="p-10 rounded-3xl bg-gradient-to-br from-green-900/40 via-zinc-900 to-black border-2 border-green-500 relative transform md:-translate-y-4 shadow-[0_0_30px_rgba(34,197,94,0.15)]">
               <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-green-500 text-black px-4 py-1 rounded-full text-sm font-black uppercase tracking-widest">Most Popular</div>
               <h3 className="text-2xl font-bold mb-2 text-green-500">Yearly Beast</h3>
               <div className="text-6xl font-black text-white mb-2">₹8,999<span className="text-xl text-zinc-400 font-normal">/yr</span></div>
               <p className="text-green-100/70 mb-8 pb-8 border-b border-white/10">Save ₹2,989 instantly with this plan.</p>
               <ul className="space-y-4 mb-8">
                 {['Everything in Monthly', '2 Months Absolutely FREE', '2 Personal Training Sessions', 'VIP Guest Passes (2/month)', 'Premium Support'].map((f, i) => (
                   <li key={i} className="flex items-center gap-3 text-white font-medium"><CheckCircle2 size={20} className="text-green-500" /> {f}</li>
                 ))}
               </ul>
               {/* === 3. Trigger added here === */}
               <button onClick={handleOpenForm} className="w-full py-4 rounded-xl bg-green-500 text-black font-black text-lg hover:bg-green-400 transition-colors shadow-lg shadow-green-500/20">Become a Beast</button>
             </motion.div>
          </div>
        </div>
      </section>

      {/* Footer (triggers added) */}
      <footer id="contact" className="bg-black pt-20 pb-10 px-6 lg:px-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 text-3xl font-black text-green-500 tracking-tighter uppercase mb-6">
              <Dumbbell size={32} /> Fit<span className="text-white">Core</span>
            </div>
            <p className="text-zinc-400 max-w-sm mb-6 leading-relaxed">We are more than a gym; we are a community dedicated to building stronger bodies and sharper minds.</p>
            {/* === 3. Trigger added here === */}
            <button onClick={handleOpenForm} className="text-green-500 font-bold flex items-center gap-2 group">Contact to own this demo <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform"/></button>
          </div>
          
          <div>
            <h4 className="text-lg font-bold mb-6 uppercase tracking-wider">Quick Links</h4>
            <ul className="space-y-3 text-zinc-400">
              <li><a href="#" className="hover:text-green-500 transition-colors">Home</a></li>
              {/* === NAYA: Click to open form === */}
              <li><a href="#" onClick={handleOpenForm} className="hover:text-green-500 transition-colors">Become Member</a></li>
              <li><a href="#plans" className="hover:text-green-500 transition-colors">Plans</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-bold mb-6 uppercase tracking-wider">Visit Us</h4>
            <ul className="space-y-4 text-zinc-400">
              <li className="flex items-start gap-3"><MapPin size={20} className="text-green-500 shrink-0 mt-1" /><span>Shop 42, Main Road, City, 201001</span></li>
              <li className="flex items-center gap-3"><Phone size={20} className="text-green-500 shrink-0" /><a href="tel:+919876543210" className="hover:text-white transition-colors">+91 98765 43210</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto pt-8 border-t border-white/10 text-center flex flex-col md:flex-row justify-between items-center text-zinc-500 text-sm">
          <p>© {new Date().getFullYear()} FitCore Gym. All rights reserved.</p>
          <p className="mt-2 md:mt-0">Designed by ProjectInfotech.in for Demos.</p>
        </div>
      </footer>

      {/* === 4. NAYA: Glassmorphism Popup Form === */}
      <AnimatePresence>
        {isFormOpen && (
          <JoinNowForm onClose={handleCloseForm} />
        )}
      </AnimatePresence>
    </div>
  );
};


// ==========================================
// Internal Component for the Popup Form
// ==========================================
interface FormProps { onClose: () => void; }

const JoinNowForm: React.FC<FormProps> = ({ onClose }) => {
  
  // Form submission handler (just for demo)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("This is a demo. In a real website, this would send an email/message to the gym owner.");
    onClose(); // Form close kardo submit ke baad
  };

  return (
    <motion.div
      // Background Overlay (Semi-transparent & Blur)
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-lg flex items-center justify-center p-4"
      onClick={onClose} // Overlay click karne pe close ho jaye
    >
      {/* Form Content Block (Glassmorphism look) */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 50 }}
        transition={{ type: "spring", damping: 20, stiffness: 300 }}
        className="relative bg-zinc-900 border border-white/10 rounded-3xl w-full max-w-xl p-8 lg:p-12 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()} // Form ke andar click karne pe close na ho
      >
        {/* Glow decorative element */}
        <div className="absolute top-0 right-0 w-60 h-60 bg-green-500/10 rounded-full blur-[80px] -z-10"></div>

        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 text-zinc-500 hover:text-white transition-colors p-2 rounded-full hover:bg-white/10"
        >
          <X size={24} />
        </button>

        {/* Form Header */}
        <div className="flex items-center gap-3 text-green-500 mb-4">
            <Mail size={28}/>
            <h2 className="text-3xl font-black uppercase tracking-tight text-white">Get <span className="text-green-500">Started</span></h2>
        </div>
        <p className="text-zinc-400 mb-10 leading-relaxed max-w-sm">Leave your details below. Our team will contact you within 24 hours to book your free trial.</p>

        {/* Actual Form */}
        <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
          
          {/* Name Input */}
          <div className="space-y-2">
            <label htmlFor="name" className="font-bold text-sm text-zinc-300 uppercase tracking-wide">Full Name</label>
            <input 
              type="text" 
              id="name"
              required
              placeholder="E.g. Rahul Kumar"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-4 px-5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-green-500/50 focus:ring-1 focus:ring-green-500/50 transition-all"
            />
          </div>

          {/* Contact Input (Phone or Email) */}
          <div className="space-y-2">
            <label htmlFor="contact" className="font-bold text-sm text-zinc-300 uppercase tracking-wide">Phone Number or Email</label>
            <input 
              type="text" 
              id="contact"
              required
              placeholder="How can we reach you?"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-4 px-5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-green-500/50 focus:ring-1 focus:ring-green-500/50 transition-all"
            />
          </div>

          {/* Hidden Demo Text */}
          <p className="text-xs text-zinc-600">This form is part of the ProjectInfotech.in demo portfolio.</p>

          {/* Submit Button */}
          <button 
            type="submit"
            className="w-full bg-green-500 text-black font-black text-xl py-5 rounded-2xl hover:bg-green-400 transition-all shadow-lg shadow-green-500/20 active:scale-95 flex items-center justify-center gap-3 group"
          >
            Claim Free Trial <ArrowRight size={20} className="group-hover:translate-x-1.5 transition-transform" />
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default BasicGymLanding;